import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import DeleteIcon from '@material-ui/icons/RemoveCircleOutline';
import IconButton from '@material-ui/core/IconButton';
import InputLabel from '@material-ui/core/InputLabel';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { VseSelectField } from '../utils/VseSelectField';
import { VseTextField } from '../utils/VseTextField';
import { VseClassComponentEditTemplate, VseClassComponentScheduleEditTemplate } from "../types/VseClassEditTemplate";
import {
  TeachingModeEntity,
  TimeZoneEntity,
} from '../types';

const useStyles = makeStyles(() => ({
  row: {
    verticalAlign: 'text-top',
  },
  cellComponentType: {
    width: '30%',
  },
  cellTeachingMode: {
    width: '16%',
  },
  cellSchedule: {
    width: '30%',
  },
  cellTimeZone: {
    width: '15%',
  },
  hidden: {
    display: 'none',
  },
}));

export type ComponentErrorKeyResolver = (index: number, key: keyof VseClassComponentEditTemplate) => string;

export type ComponentScheduleErrorKeyResolver = (index: number, scheduleIndex: number, key: keyof VseClassComponentScheduleEditTemplate) => string;

let teachingModes: TeachingModeEntity[] = [];
let tzCodes: TimeZoneEntity[] = [];

export interface VseClassComponentEditTableProps {
  list: Array<VseClassComponentEditTemplate>,
  readOnly?: boolean,
  teachingModeOptions: TeachingModeEntity[],
  timeZoneOptions: TimeZoneEntity[],
  onChange: (compIndex: number, changes: Partial<VseClassComponentEditTemplate>) => void
  onAdd: () => void,
  onDelete: (compIndex: number) => void,
  onScheduleChange: (compIndex: number, schdIndex: number, changes: Partial<VseClassComponentScheduleEditTemplate>) => void,
  onScheduleAdd: (compIndex: number) => void,
  onScheduleDelete: (compIndex: number, schdIndex: number) => void,
  resolveComponentErrorKey: ComponentErrorKeyResolver,
  resolveComponentScheduleErrorKey: ComponentScheduleErrorKeyResolver,
  valProblemKeys?: Array<string>,
}

function renderScheduleCells(
  classes: ReturnType<typeof useStyles>,
  schedule: VseClassComponentScheduleEditTemplate,
  onChange: (changes: Partial<VseClassComponentScheduleEditTemplate>) => void,
  onAdd: () => void,
  onDelete: () => void,
  index: number,
  last: boolean,
  readOnly: boolean,
  resolveScheduleErrorKey: (index: number, key: keyof VseClassComponentScheduleEditTemplate) => string,
  valProblemKeys?: Array<string>,
) {
  function hasError(key: keyof VseClassComponentScheduleEditTemplate): boolean {
    if(valProblemKeys){
      const searchElement: string = resolveScheduleErrorKey(index, key);
      return valProblemKeys.includes(searchElement);
    }
    return false;
  }
  return (
    <React.Fragment>
      <TableCell padding='checkbox'>
        {!readOnly && (
          <IconButton
            className={clsx(!last && classes.hidden)}
            aria-label='Add Schedule'
            color='secondary'
            onClick={() => onAdd()}
          >
            <AddIcon />
          </IconButton>
        )}
        {!readOnly && (
          <IconButton
            aria-label='Delete Schedule'
            onClick={() => onDelete()}
          >
            <DeleteIcon />
          </IconButton>
        )}
      </TableCell>
      <TableCell>
        <VseTextField
          placeholder="(e.g. Tue, Thu 1430-1630, TBC, etc.)"
          value={schedule.description}
          readOnly={readOnly}
          onChange={(e) => onChange({ description: e.target.value })}
          error={hasError('description')}
        />
      </TableCell>
      <TableCell>
        <VseSelectField
          value={schedule.tzCode}
          required
          readOnly={readOnly}
          options={tzCodes}
          onChange={(e) => onChange({ tzCode: e.target.value })}
          error={hasError('tzCode')}
        />
      </TableCell>
    </React.Fragment>
  );
}

function renderComponent(
  classes: ReturnType<typeof useStyles>,
  component: VseClassComponentEditTemplate,
  onChange: (changes: Partial<VseClassComponentEditTemplate>) => void,
  onAdd: () => void,
  onDelete: () => void,
  onScheduledChange: (schdIndex: number, changes: Partial<VseClassComponentScheduleEditTemplate>) => void,
  onScheduleAdd: () => void,
  onScheduleDelete: (schdIndex: number) => void,
  index: number,
  last: boolean,
  readOnly: boolean,
  resolveComponentErrorKey: ComponentErrorKeyResolver,
  resolveComponentScheduleErrorKey: ComponentScheduleErrorKeyResolver,
  valProblemKeys?: Array<string>,
) {
  function hasError(key: keyof VseClassComponentEditTemplate): boolean {
    if (valProblemKeys) {
      const searchElement: string = resolveComponentErrorKey(index, key);
      return valProblemKeys.includes(searchElement);
    }
    return false;
  }
  const [firstSchd, ...otherSchd] = component.schedule;
  return (
    <TableBody key={index}>
      <TableRow className={classes.row} key={`${index}-1`}>
        {!readOnly && (
          <TableCell padding='checkbox' rowSpan={component.schedule.length}>
              <IconButton
                className={clsx(!last && classes.hidden)}
                aria-label='Add Component'
                color='secondary'
                onClick={() => onAdd()}
              >
                <AddIcon />
              </IconButton>
              <IconButton
                aria-label='Delete Component'
                onClick={() => onDelete()}
              >
                <DeleteIcon />
              </IconButton>
            </TableCell>
        )}
        <TableCell rowSpan={component.schedule.length}>
          <VseTextField
            placeholder="(e.g. Lecture / Tutorial)"
            value={component.componentType}
            required
            readOnly={readOnly}
            onChange={(e) => onChange({ componentType: e.target.value })}
            error={hasError('componentType')}
          />
        </TableCell>
        <TableCell rowSpan={component.schedule.length}>
          <VseSelectField
            value={component.teachingMode}
            required
            readOnly={readOnly}
            options={teachingModes}
            onChange={(e) => onChange({ teachingMode: e.target.value })}
            error={hasError('teachingMode')}
          />
        </TableCell>
        {renderScheduleCells(classes, firstSchd, (changes) => onScheduledChange(0, changes), onScheduleAdd, () => onScheduleDelete(0), 0, otherSchd.length === 0, readOnly, (scheduleIndex, key) => resolveComponentScheduleErrorKey(index, scheduleIndex, key), valProblemKeys)}
      </TableRow>
      {otherSchd.map((value, j) =>
        <TableRow key={`${index}-${j + 2}`}>
          {renderScheduleCells(classes, value, (changes) => onScheduledChange(j + 1, changes), onScheduleAdd, () => onScheduleDelete(j + 1), (j + 1), otherSchd.length === (j + 1), readOnly, (scheduleIndex, key) => resolveComponentScheduleErrorKey(index, scheduleIndex, key), valProblemKeys)}
        </TableRow>
      )}
    </TableBody>
  );
}

function renderComponents(
  classes: ReturnType<typeof useStyles>,
  components: Array<VseClassComponentEditTemplate>,
  readOnly: boolean,
  onComponentChange: (compIndex: number, changes: Partial<VseClassComponentEditTemplate>) => void,
  onComponentAdd: () => void,
  onComponentDelete: (compIndex: number) => void,
  onScheduleChange: (compIndex: number, schdIndex: number, changes: Partial<VseClassComponentScheduleEditTemplate>) => void,
  onScheduleAdd: (compIndex: number) => void,
  onScheduleDelete: (compIndex: number, schdIndex: number) => void,
  resolveComponentErrorKey: ComponentErrorKeyResolver,
  resolveComponentScheduleErrorKey: ComponentScheduleErrorKeyResolver,
  valProblemKeys?: Array<string>,
) {
  return components.map((value, index) => renderComponent(
    classes,
    value,
    (changes) => onComponentChange(index, changes),
    onComponentAdd,
    () => onComponentDelete(index),
    (schdIndex, changes) => onScheduleChange(index, schdIndex, changes),
    () => onScheduleAdd(index),
    (schdIndex) => onScheduleDelete(index, schdIndex),
    index,
    components.length === (index + 1),
    readOnly,
    resolveComponentErrorKey,
    resolveComponentScheduleErrorKey,
    valProblemKeys,
  ));
}

export function VseClassComponentEditTable(props: VseClassComponentEditTableProps) {
  const classes = useStyles();
  const {
    readOnly = false,
  } = props;
  teachingModes = props.teachingModeOptions;
  tzCodes = props.timeZoneOptions;

  return (
    <Table>
      <TableHead>
        <TableRow>
          {!readOnly && <TableCell padding='checkbox' />}
          <TableCell className={classes.cellComponentType}>
            <InputLabel required>Component</InputLabel>
          </TableCell>
          <TableCell className={classes.cellTeachingMode}>
            <InputLabel required>Mode of Teaching</InputLabel>
          </TableCell>
          <TableCell padding='checkbox' />
          <TableCell className={classes.cellSchedule}>
            <InputLabel required>Schedule Description</InputLabel>
          </TableCell>
          <TableCell className={classes.cellTimeZone}>
            <InputLabel required>Time Zone</InputLabel>
          </TableCell>
        </TableRow>
      </TableHead>
      {renderComponents(
        classes,
        props.list,
        readOnly,
        props.onChange,
        props.onAdd,
        props.onDelete,
        props.onScheduleChange,
        props.onScheduleAdd,
        props.onScheduleDelete,
        props.resolveComponentErrorKey,
        props.resolveComponentScheduleErrorKey,
        props.valProblemKeys,
      )}
    </Table>
  );
}
